import React, { useState, useEffect, useCallback } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ProductFormModal from './ProductFormModal';
import config, { resolveImagenUrl } from '../../config/apiConfig';
import imgDefault from '../../imgs/defecto.png'; // Asegúrate que la ruta sea correcta
import {
    obtenerTodosLosProductos,
    eliminarProducto,
    ajusteRapidoStock // Asegúrate de que esta función exista en productosApi.js
    , actualizarOrdenVisual
} from '../../services/productosApi';

const AdministradorProductos = () => {
    const [products, setProducts] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [modalVisible, setModalVisible] = useState(false);
    const [productToEdit, setProductToEdit] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");

    const fetchProducts = useCallback(async () => {
        setIsLoading(true);
        try {
            const data = await obtenerTodosLosProductos();
            setProducts(data || []);
        } catch (error) {
            toast.error("Error al cargar productos");
            setProducts([]);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchProducts();
    }, [fetchProducts]);

    const handleOpenModal = (product = null) => {
        setProductToEdit(product);
        setModalVisible(true);
    };

    const handleCloseModal = () => {
        setModalVisible(false);
        setProductToEdit(null);
    };

    const handleSaveProduct = async () => {
        setModalVisible(false);
        setProductToEdit(null);
        await fetchProducts();
        toast.success("¡Lista actualizada!");
    };

    const handleDeleteProduct = async (product) => {
        if (!product || !product.id) return;

        const nombreProd = product.nombre || product.name || "este producto";
        if (!window.confirm("¿Estás seguro de eliminar \"" + nombreProd + "\"?")) return;

        try {
            await eliminarProducto(product.id);
            toast.success("Producto eliminado");
            fetchProducts();
        } catch (error) {
            toast.error("No se pudo eliminar el producto");
        }
    };


    // --- LÓGICA: AJUSTE RÁPIDO DE INVENTARIO ---
    const handleAjusteStock = async (product, delta) => {
        if (!product.controlar_stock) return;

        // Optimistic UI: Actualiza visualmente al instante
        const nuevoStock = parseFloat(product.stock || 0) + delta;

        setProducts(prev => prev.map(p =>
            p.id === product.id ? { ...p, stock: nuevoStock } : p
        ));

        try {
            // Llama al backend
            await ajusteRapidoStock(product.id, nuevoStock);
        } catch (error) {
            // Si falla en el servidor, devolvemos el valor original recargando la tabla
            toast.error("Error al ajustar stock de " + (product.nombre || product.name));
            fetchProducts();
        }
    };

    const filteredProducts = products.filter(function (p) {
        const nombre = p.nombre || p.name || "";
        return nombre.toLowerCase().indexOf(searchTerm.toLowerCase()) !== -1;
    });

    const groupedProducts = React.useMemo(() => {
        const groups = filteredProducts.reduce((acc, p) => {
            const catId = p.categoria_id || 'sin-categoria';
            const catNombre = p.nombre_categoria || 'General';
            if (!acc[catId]) {
                acc[catId] = { nombre: catNombre, items: [] };
            }
            acc[catId].items.push(p);
            return acc;
        }, {});

        // ORDENAR CADA GRUPO
        Object.keys(groups).forEach(key => {
            groups[key].items.sort((a, b) => {
                const ordenA = parseInt(a.orden_visual || 0);
                const ordenB = parseInt(b.orden_visual || 0);

                if (ordenA !== ordenB) {
                    return ordenA - ordenB;
                }
                return a.id - b.id; // Desempate por ID
            });
        });

        return groups;
    }, [filteredProducts]);

    // Helper para el Semáforo
    const getSemaforoClass = (p) => {
        if (!p.controlar_stock) return 'badge-soft-secondary text-muted';
        const stock = parseFloat(p.stock || 0);
        const min = parseFloat(p.stock_minimo || 0);

        if (stock <= 0) return 'badge-soft-danger'; // Rojo
        if (stock <= min) return 'badge-soft-warning'; // Naranjo
        return 'badge-soft-success'; // Verde
    };

    const obtenerUrlImagen = (producto) => {
        // 1. Extraemos el nombre o ruta de la imagen que viene de la BD
        const nombreFoto = producto.url_imagen || producto.img || producto.imagen;

        // 2. Si no hay foto, devolvemos la de defecto (el chef)
        if (!nombreFoto || nombreFoto === "" || nombreFoto === "null" || nombreFoto === undefined) {
            return imgDefault;
        }

        // 3. resolveImagenUrl deja pasar URLs http absolutas y arma la URL de R2 para el resto
        return resolveImagenUrl(nombreFoto);
    };
  
    // --- LÓGICA: ACTUALIZAR ORDEN VISUAL RÁPIDO ---
    // --- LÓGICA: MOVER PRODUCTO (SUBIR / BAJAR) ---
    const handleMove = async (product, direction, groupItems) => {
        const index = groupItems.findIndex(p => p.id === product.id);

        // Validaciones de límites
        if (direction === 'up' && index === 0) return;
        if (direction === 'down' && index === groupItems.length - 1) return;

        // 1. Clonamos los items de la categoría y hacemos el intercambio físico en el array
        const newGroupItems = [...groupItems];
        const targetIndex = direction === 'up' ? index - 1 : index + 1;

        const temp = newGroupItems[index];
        newGroupItems[index] = newGroupItems[targetIndex];
        newGroupItems[targetIndex] = temp;

        // 2. Creamos un "mapa de actualización" asignando órdenes secuenciales (1, 2, 3...)
        // Esto elimina los "0" y obliga a React a reordenar.
        const updates = newGroupItems.map((item, idx) => ({
            id: item.id,
            nuevo_orden: idx + 1
        }));

        // 3. Actualización Visual Instantánea (State)
        setProducts(prev => prev.map(p => {
            const update = updates.find(u => u.id === p.id);
            return update ? { ...p, orden_visual: update.nuevo_orden } : p;
        }));

        // 4. Guardar en Base de Datos (Solo enviamos los dos que intercambiaron)
        try {
            const up1 = updates.find(u => u.id === product.id);
            const up2 = updates.find(u => u.id === groupItems[targetIndex].id);

            await Promise.all([
                actualizarOrdenVisual(up1.id, up1.nuevo_orden),
                actualizarOrdenVisual(up2.id, up2.nuevo_orden)
            ]);
        } catch (error) {
            console.error("Error al reordenar:", error);
            toast.error("Error al guardar el orden en el servidor");
            fetchProducts(); // Recargamos para volver a la realidad si falló
        }
    };
    return (
        <div className="bg-light min-vh-100 py-5 px-3" >
            <ToastContainer position="bottom-center" autoClose={2000} hideProgressBar />

            <div className="container" style={{ maxWidth: '1100px' }}>

                {/* CABECERA */}
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mb-5">
                    <div>
                        <h1 className="h2 font-weight-bold text-dark mb-1">Inventario de Productos</h1>
                        <p className="text-muted">Gestiona tus platillos, precios y existencias.</p>
                    </div>
                    <button className="btn btn-primary btn-lg rounded-pill shadow-lg px-4" onClick={() => handleOpenModal(null)}>
                        <i className="fas fa-plus mr-2"></i> Nuevo Producto
                    </button>
                </div>

                {/* CARD DE CONTENIDO */}
                <div className="card border-0 shadow-sm rounded-xl overflow-hidden">

                    {/* BARRA DE BÚSQUEDA */}
                    <div className="card-header bg-white border-0 pt-4 px-4">
                        <div className="input-group bg-light rounded-pill px-3 py-1 border">
                            <div className="input-group-prepend border-0 bg-transparent">
                                <span className="input-group-text bg-transparent border-0 text-muted">
                                    <i className="fas fa-search"></i>
                                </span>
                            </div>
                            <input
                                type="text" className="form-control bg-transparent border-0"
                                placeholder="Buscar por nombre..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>

                    {/* TABLA DE PRODUCTOS CON SCROLL */}
                    <div className="card-body p-0">
                        <div className="table-responsive custom-scrollbar" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
                            <table className="table table-hover align-middle mb-0" style={{ position: 'relative' }}>
                                <thead className="bg-light" style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                                    <tr>
                                        <th className="border-0 py-3 text-muted small text-uppercase shadow-sm-bottom" style={{ width: '80px' }}>Orden</th>
                                        <th className="border-0 px-4 py-3 text-muted small text-uppercase shadow-sm-bottom">Producto</th>
                                        <th className="border-0 py-3 text-muted small text-uppercase shadow-sm-bottom">Categoría</th>
                                        <th className="border-0 py-3 text-muted small text-uppercase shadow-sm-bottom">Precio</th>
                                        <th className="border-0 py-3 text-muted small text-uppercase text-center shadow-sm-bottom" style={{ width: '180px' }}>Inventario</th>
                                        <th className="border-0 px-4 py-3 text-right text-muted small text-uppercase shadow-sm-bottom">Opciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {isLoading ? (
                                        <tr><td colSpan="5" className="text-center py-5">Cargando inventario...</td></tr>
                                    ) : (
                                        Object.entries(groupedProducts).map(([catId, grupo]) => (
                                            <React.Fragment key={catId}>
                                                {/* FILA SEPARADORA DE CATEGORÍA */}
                                                <tr className="bg-light">
                                                    <td colSpan="5" className="px-4 py-2" style={{ backgroundColor: '#f1f5f9' }}>
                                                        <span className="badge badge-primary rounded-pill mr-2">{grupo.items.length}</span>
                                                        <strong className="text-uppercase small text-primary">{grupo.nombre}</strong>
                                                    </td>
                                                </tr>

                                                {/* PRODUCTOS DE ESTA CATEGORÍA */}

                                                {grupo.items.map((p, idx) => (
                                                    <tr key={p.id}>
                                                        {/* 1. COLUMNA DE REORDENAMIENTO (Botones Arriba/Abajo) */}
                                                        <td className="pl-4 py-3">
                                                            <div className="btn-group-vertical shadow-sm" style={{ borderRadius: '8px', overflow: 'hidden' }}>
                                                                <button
                                                                    className="btn btn-xs btn-white border-0 py-0"
                                                                    style={{ height: '22px', backgroundColor: '#fff' }}
                                                                    onClick={() => handleMove(p, 'up', grupo.items)}
                                                                    disabled={idx === 0}
                                                                >
                                                                    <i className={`fas fa-chevron-up ${idx === 0 ? 'text-muted' : 'text-primary'}`} style={{ fontSize: '10px' }}></i>
                                                                </button>
                                                                <div style={{ borderTop: '1px solid #eee' }}></div>
                                                                <button
                                                                    className="btn btn-xs btn-white border-0 py-0"
                                                                    style={{ height: '22px', backgroundColor: '#fff' }}
                                                                    onClick={() => handleMove(p, 'down', grupo.items)}
                                                                    disabled={idx === grupo.items.length - 1}
                                                                >
                                                                    <i className={`fas fa-chevron-down ${idx === grupo.items.length - 1 ? 'text-muted' : 'text-primary'}`} style={{ fontSize: '10px' }}></i>
                                                                </button>
                                                            </div>
                                                            {/* Pequeño indicador del número por si quieres saberlo */}
                                                            <div className="text-center text-muted mt-1" style={{ fontSize: '10px' }}>#{p.orden_visual}</div>
                                                        </td>

                                                        {/* 2. COLUMNA PRODUCTO */}
                                                        <td className="px-2 py-3">
                                                            <div className="d-flex align-items-center">
                                                                <div className="bg-light rounded-lg mr-3 shadow-sm" style={{ width: '45px', height: '45px', overflow: 'hidden', flexShrink: 0 }}>
                                                                    <img
                                                                        src={obtenerUrlImagen(p)}
                                                                        alt=""
                                                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                                        onError={(e) => { e.target.src = imgDefault; }}
                                                                    />
                                                                </div>
                                                                <span className="font-weight-bold text-dark text-wrap" style={{ maxWidth: '250px' }}>
                                                                    {p.nombre}
                                                                </span>
                                                            </div>
                                                        </td>

                                                        {/* 3. CATEGORÍA */}
                                                        <td className="py-3 text-muted small">
                                                            {grupo.nombre}
                                                        </td>

                                                        {/* 4. PRECIO */}
                                                        <td className="py-3 font-weight-bold text-dark">
                                                            {new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', minimumFractionDigits: 0 }).format(p.precio || 0)}
                                                        </td>

                                                        {/* 5. INVENTARIO */}
                                                        <td className="py-3 text-center">
                                                            {p.controlar_stock ? (
                                                                <div className="d-flex align-items-center justify-content-center">
                                                                    <button className="btn btn-sm btn-light border" onClick={() => handleAjusteStock(p, -1)}>-</button>
                                                                    <span className={`badge px-2 py-2 mx-2 rounded-pill shadow-sm ${getSemaforoClass(p)}`} style={{ minWidth: '60px' }}>
                                                                        {parseFloat(p.stock || 0)}
                                                                    </span>
                                                                    <button className="btn btn-sm btn-light border" onClick={() => handleAjusteStock(p, 1)}>+</button>
                                                                </div>
                                                            ) : (
                                                                <span className="badge badge-soft-secondary px-3 py-2 rounded-pill">S/C</span>
                                                            )}
                                                        </td>

                                                        {/* 6. OPCIONES */}
                                                        <td className="px-4 py-3 text-right">
                                                            <button className="btn btn-icon btn-light rounded-circle mr-2" onClick={() => handleOpenModal(p)}>
                                                                <i className="fas fa-pen text-primary"></i>
                                                            </button>
                                                            <button className="btn btn-icon btn-light rounded-circle" onClick={() => handleDeleteProduct(p)}>
                                                                <i className="fas fa-trash text-danger"></i>
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </React.Fragment>
                                        ))
                                    )}
                                </tbody>
                            </table>
                            {!isLoading && filteredProducts.length === 0 && (
                                <div className="text-center py-5 text-muted">No se encontraron productos.</div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* ESTILOS */}
            <style jsx>{`
                .rounded-xl { border-radius: 1.25rem !important; }
                .rounded-lg { border-radius: 0.75rem !important; }
                .badge-soft-success { background-color: #e6fcf5; color: #087f5b; }
                .badge-soft-warning { background-color: #fff3cd; color: #856404; }
                .badge-soft-danger { background-color: #fff5f5; color: #c92a2a; }
                .badge-soft-secondary { background-color: #f8f9fa; color: #6c757d; }
                .btn-icon { width: 38px; height: 38px; display: inline-flex; align-items: center; justify-content: center; border: none; transition: 0.2s; }
                .btn-icon:hover { background-color: #eef2f7; transform: translateY(-2px); }
                .table-hover tbody tr:hover { background-color: #fbfbfb; }
                
                .shadow-sm-bottom {
                    box-shadow: 0 2px 4px rgba(0,0,0,0.04);
                    background-color: #f8f9fa !important;
                }

                .custom-scrollbar::-webkit-scrollbar { width: 8px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background-color: #cbd5e1; border-radius: 20px; }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover { background-color: #94a3b8; }
            `}</style>

            <ProductFormModal
                isVisible={modalVisible}
                onClose={handleCloseModal}
                onSave={handleSaveProduct}
                productToEdit={productToEdit}
                allProducts={products}
            />
        </div>
    );
};

export default AdministradorProductos;